/**
 * **The token command menu's tree** — `projects/token-command-menu.md` item 1.2, the pure half of
 * a unit's one action surface, docked in the unit column above the unit panel (`roadmap/design-combat-token-hud.md`, `projects/unit-panel.md`).
 *
 * {@link commandMenuTree} turns a unit's action model ({@link UnitActionData}, the derivation the
 * mech sheet's retired QUICK / FULL grid read; mechs, NPCs and pilots alike) into drill-down screens: Root → five groups
 * (Attack, Hack, Maneuver, Use Equipment, Mech System, the developer's R6 ruling; an NPC's read
 * Attack, Hack, Maneuver, Features, System, `npc-command-menu` Q1; a pilot's four read Attack,
 * Maneuver, Gear, Mech, `pilot-command-menu` Q1) → their rows →
 * the multi-step children (Skirmish and Barrage mounts, Quick and Full Tech options). Every row is
 * priced in diamonds and knows why it is greyed. Nothing here renders or dispatches; the renderer
 * (`TokenCommandMenu.tsx`, run by `TokenCommandMenuHost.tsx`) maps a row onto the model's handlers.
 *
 * **Say it once (`projects/menu-advisory-diet.md`).** A reason that holds for the whole unit (its
 * turn isn't open, it is Stunned or Shut Down, it holds a prepared action) is the tree's one
 * {@link CommandMenuTree.unitLine}; a row it blocks is `blockedBy: "unit"` and shows no line of its
 * own. A row shows a reason only when it is the row's own (Discharged, Unloaded, uses left), and a
 * turn-scoped one ("Already used this turn", the budget) only while the turn is open.
 *
 * **It mirrors, it never enforces.** Each reason comes from the wall the server applies — the
 * held-action lockout and the status wall ({@link actionStatusBlockReason}, whose own exceptions
 * decide which rows a Stunned unit keeps), the descriptors' once-per-turn `usedUp`, the Protocol
 * window (`isTurnVirgin`, the rules' own door), and for mounts {@link classifySkirmishCard} plus
 * {@link weaponCardTitle}, so an Ordnance or Loading reason reads word for word as a weapon card
 * ({@link weaponCardTitle}) does.
 *
 * **A `ready` unit reads through the pre-activation fold**: every economy read composes against
 * `model.economy.state`, `previewActivation`'s hypothetical post-activation state when the viewer
 * controls a ready unit.
 *
 * **The groups are mapped here, not on `MechActionCategory`** — that union is an action row's chip
 * (`SUPPORT`, `MOVE`, …) and does not line up with the menu (Search is TECH but sits under
 * Maneuver; Engage COMP/CON is SUPPORT but sits under Use Equipment). Widening it would couple the
 * two renderers.
 */
import type { Compendium } from "@lancer-vtt/compendium";
import type { Result } from "@lancer-vtt/shared";
import { type AttackLock, type BracedTurnLimit, type Combatant, bracedTurnLimit, type EncounterState, type HeldAction, type WeaponMode, type WeaponProfile, COMPCON_ENGAGED_STATUS, DOWN_AND_OUT_STATUS, RODEO_STATUS, SHUT_DOWN_STATUS, hasStatus, isItemDestroyed, isStunned, latchedWeaponMode, threatValue, weaponInstanceKey, WEAPON_SIZE_SUPERHEAVY } from "@lancer-vtt/domain";
import { FALLBACK_WEAPON, type FeatureState, checkGrappleBreakLegality, checkGrappleContestLegality, checkJockeyLegality, checkJockeyMenuLegality, checkMountLegality, checkOrdnanceWindow, checkReloadLegality, checkRideLegality, checkShakeOffLegality, checkStopBarrageAsSkirmish, featureState, grapplePartner, intentCost, isAttackShapedHeld, isRiderShot, isTurnVirgin, jockeyPartner, jockeyedRiderOf, legalQuickTechOptions, lockPairingBlock, techMenuPick, mountKeyOf, overchargeStep, pairedAuxRiderOf, ridePartner, weaponAttackCount, weaponFireBlock, attacksTargets, type LoadedOverlay } from "@lancer-vtt/rules";
import { actionStatusBlockReason } from "./actionStatusMirror";
import type { CommandMenuState } from "./commandMenuActions";
import { buildAttackIntent, buildDetectMineIntent, buildDisarmMineIntent, buildJockeyMenuIntent, buildShakeOffIntent } from "./attackIntent";
import { targetsInReach } from "./attackRange";
import { classifySkirmishCard } from "./combatWindows/chooserClassify";
import { heldSummaryLine } from "./combatWindows/inspectorPanels";
import { grantedTargetingFireable, isProtocolCost, type UnitActionView } from "./combatWindows/unitActions";
import type { JockeyMenuChoice, UnitActionData } from "./combatWindows/useUnitActionModel";
import { weaponCardTitle, type WeaponCardState } from "./combatWindows/WeaponCard";
import { formatTagChip, formatWeaponDamage, formatWeaponRange } from "./weaponFormat";
import type { GlossaryKey } from "../glossary/combatGlossary";
import { asNpcTier, interpolateTierTokens } from "../npcTierText";
import { actionsLeftToEnd } from "./useConfirmEndTurn";

/** The five R6 groups, in menu order. */
export type CommandMenuGroupId = "attack" | "hack" | "maneuver" | "equipment" | "system";

/**
 * The actions whose chooser the menu hosts on its own card (3.2): a form collected up front, then
 * one commit button, with no board target (Stabilize, Search, Self-Destruct, Prepare and Disarm
 * Mine; a pilot's Mount, Jockey and Ride, `pilot-command-menu` 1.4).
 */
export type CommandMenuChooserId = "stabilize" | "search" | "self_destruct" | "prepare" | "disarm_mine" | "mount" | "jockey" | "ride";

/** The chooser cards' action ids, in the tree's row id space. */
export const COMMAND_MENU_CHOOSERS: readonly CommandMenuChooserId[] = ["stabilize", "search", "self_destruct", "prepare", "disarm_mine", "mount", "jockey", "ride"];

/**
 * Every screen the tree can hold: the root, the five groups, the four multi-step children, the
 * fire card (`fire`, R7): the committed mounts' weapons of a Skirmish or Barrage, and a hosted
 * chooser's card while its action is armed. A pilot's Fight uses the `skirmish` screen (its weapon
 * screen, titled Fight), so the fire card and its aims serve both; `reload` is a pilot's Reload
 * card while two or more Loading weapons are unloaded.
 */
export type CommandMenuScreenId = "root" | CommandMenuGroupId | "skirmish" | "barrage" | "quick_tech" | "full_tech" | "fire" | "boost" | "reload" | CommandMenuChooserId;

/** One weapon on a mount row, as the Skirmish card shows it. */
export type CommandMenuWeapon = {
  /** The weapon **instance key** — what a fire intent names. */
  key: string;
  name: string;
  range: string;
  damage: string;
  /** `rule` is the tag's rule text from the compendium's tag entry (`lancer-data` `tags.json`), `{VAL}` filled in. */
  tags: { id: string; label: string; val?: number; rule?: string }[];
  /** {@link classifySkirmishCard}'s verdict. `"armable"` is the only fireable state. */
  state: WeaponCardState;
  /** The weapon card's own title for a blocked weapon ({@link weaponCardTitle}); absent when armable. */
  reason?: string;
  /** {@link weaponBlockShort}: the on-screen form of {@link reason} ("Unloaded · Stabilize to reload"). */
  reasonShort?: string;
  /** A ranged-or-melee weapon (the Ram Cannon): both profiles, or the mode it is held to this turn ({@link weaponModeLine}). */
  mode?: string;
};

/**
 * A group screen's row section (`decisions/0186` item 2): the group's Protocols ("Start of turn")
 * first, then its Free rows, then its Quick and Full rows. {@link sectionOf} derives it.
 */
export type CommandMenuSection = "start" | "free" | "action";

/**
 * The section headings' copy, in one place: `start` and `free` sections carry a heading, `action`
 * rows none. `combat-glossary` wraps these terms in its `<Term>`; keep every section string here.
 */
export const COMMAND_MENU_SECTION_HEADING: Readonly<Record<Exclude<CommandMenuSection, "action">, string>> = {
  start: "Start of turn",
  free: "Free",
};

/** The glossary term each section heading opens (`combat-glossary` 3.1): Start of turn holds the Protocols. */
export const COMMAND_MENU_SECTION_TERM: Readonly<Record<Exclude<CommandMenuSection, "action">, GlossaryKey>> = {
  start: "type:protocol",
  free: "type:free",
};

/** The glossary terms in the menu's head and root legend: the action diamonds, the MOVE count, the PROTOCOLS legend. */
export const COMMAND_MENU_HEAD_TERM = {
  actions: "type:actions",
  move: "type:move",
  protocols: "type:protocol",
} as const satisfies Record<string, GlossaryKey>;

/**
 * What a group card shows in place of its Start of turn section once the unit has moved or acted
 * this turn ({@link CommandMenuTree.protocolWindowClosed}): Protocols are start-of-turn only
 * (Core p.62), so they are back next turn.
 */
export const START_OF_TURN_CLOSED_NOTE = "back next turn";
export const START_OF_TURN_CLOSED = `${COMMAND_MENU_SECTION_HEADING.start}: ${START_OF_TURN_CLOSED_NOTE}`;

/** A mount row's reach line ({@link CommandMenuRow.inReach}): "2 in reach", or "none in reach". */
export function inReachLine(n: number): string {
  return n === 0 ? "none in reach" : `${n} in reach`;
}

/** A group-screen row's section: `start` for a Protocol, `free` for a Free row, `action` for the rest. */
export function sectionOf(row: Pick<CommandMenuRow, "protocol" | "free">): CommandMenuSection {
  return row.protocol ? "start" : row.free ? "free" : "action";
}

const SECTION_RANK: Readonly<Record<CommandMenuSection, number>> = { start: 0, free: 1, action: 2 };

export type CommandMenuRow = {
  /** Unique within its screen. */
  id: string;
  /**
   * `group` drills into a group screen; `action` is an action (it may still drill, see `child`);
   * `mount` is a Skirmish/Barrage mount; `option` is a Quick/Full Tech option; `weapon` is one
   * weapon on the fire card.
   */
  kind: "group" | "action" | "mount" | "option" | "weapon";
  label: string;
  /** Mount rows: the mount's own name ("Heavy mount"), rendered muted. */
  secondary?: string;
  /** The action's rules blurb (core table or compendium text). */
  blurb?: string;
  /** A derived one-liner the row must state: Overcharge's heat cost. */
  detail?: string;
  /**
   * What the renderer acts on: the descriptor id for an `action` (core `"boost"`, granted
   * `"<itemId>:<n>"`), the {@link mountKeyOf} key for a `mount`, the option id for an `option`.
   * Absent on `group` rows.
   */
  actionId?: string;
  /** The screen this row opens, when it drills down. */
  child?: CommandMenuScreenId;
  /** Diamonds the row spends: Quick 1, Full 2, Free / Protocol 0. 0 on groups and pick-screen toggles. */
  diamonds: number;
  /** A Protocol (free, start of turn only). */
  protocol: boolean;
  /** A Free action row (0 diamonds, not a Protocol, not a group or toggle): the FREE chip. */
  free: boolean;
  available: boolean;
  /** Why the row is greyed. Present exactly when `available` is false, never empty. */
  reason?: string;
  /**
   * Greyed without a reason of its own, so the row shows no line and no tooltip: `unit` by the unit
   * wall alone ({@link CommandMenuTree.unitLine} says why, and {@link reason} is that line as the
   * row's accessible label), `children` for a drilling row whose children are greyed for different
   * reasons (its card says why, row by row).
   */
  blockedBy?: "unit" | "children";
  /**
   * Greyed only by the diamond budget (the unit can't afford it). The row shows no reason line or
   * tooltip: its cost diamonds say it (held filled, missing hollow), and {@link reason} is its
   * short accessible label.
   */
  cantAfford?: true;
  /**
   * Mount rows greyed by their own weapons: the short on-screen form of {@link reason}
   * ({@link weaponBlockShort}); the renderer keeps the full sentence as the row's title.
   */
  reasonShort?: string;
  /** Used already this turn: struck through. */
  usedThisTurn: boolean;
  /** A Recharge item's chip data ({@link featureState}): the `5+` target, and whether it is charged. */
  recharge?: { on: number; charged: boolean };
  /** Rows on the five group screens only: the row's section ({@link sectionOf}), which orders the screen. */
  section?: CommandMenuSection;
  /**
   * Mount rows on the Skirmish and Barrage cards: how many hostile units the mount can target from
   * where the unit stands, the most over its weapons ({@link targetsInReach}, the targeting wash's
   * own rule). Absent while the unit is not on the board.
   */
  inReach?: number;
  /** This row's subtree holds an available Protocol at any depth (the hollow yellow diamond on a group). */
  protocolBelow: boolean;
  /**
   * Mount rows: the mount's weapons, blocked ones greyed with their reason. Reload rows: the one
   * weapon the click reloads.
   */
  weapons?: CommandMenuWeapon[];
  /**
   * The ledger key the row marks when it resolves, where {@link usedKeyOf}'s default is wrong: a
   * pilot's Fight weapon and unarmed strike mark `fight`, a Reload card row marks `reload`.
   */
  usedKey?: string;
  /** Mount rows: a Superheavy mount, which a Barrage fires alone (Core p.71). */
  superheavy?: boolean;
  /** Weapon rows: the attack the fire card fires (the aim's mode). */
  attack?: FireAction;
  /** Weapon rows: a note the row states while fireable (the Aux rider's no-bonus-damage rule). */
  note?: string;
  /**
   * Option rows on a live Full Tech card ({@link CommandMenuScreen.fullTech}): the pick continues
   * the open lock, so choosing it arms that option's shot at once instead of toggling a pick.
   */
  continuation?: true;
};

/** The two attacks the fire card fires. */
export type FireAction = "skirmish" | "barrage";

/**
 * The menu's local commitment before the first shot (R7: nothing reaches the server until then):
 * a Skirmish's one mount, or a Barrage's confirmed mounts. Once a shot lands the server-synced
 * lock replaces it.
 */
export type FireCommit = {
  action: FireAction;
  mounts: string[];
  /**
   * A pilot's Fight (`pilot-command-menu` 2.3): its ledger key and use count when committed. A
   * Sidearm pilot's Fight stays unused after one shot (two a turn), so the commit is spent when the
   * count moves, not when the row reads used.
   */
  ledger?: { key: string; at: number };
};

/** The fire card's bottom button, by progress (the R7 table). */
export type FireExit = "cancel" | "stop_as_skirmish" | "end_early" | "stop_firing";

/** The fire card (R7, prototype `bfire`): the committed mounts as headings over their weapons. */
export type CommandMenuFire = {
  action: FireAction;
  groups: { key: string; name: string; weaponKeys: string[] }[];
  /** The weapons fired this action, off the lock. */
  fired: string[];
  /** A lock is open for this action (the first shot has landed). */
  live: boolean;
  /** Fireable weapon keys, in list order; the first is the default selection. */
  fireable: string[];
  exit: FireExit;
  /** Back is locked once the first shot has fired. */
  backLocked: boolean;
};

/** A pick-N screen (Barrage mounts, Full Tech options): rows toggle, one confirm row spends. */
export type CommandMenuPick = {
  count: 2;
  /** May the same row be picked twice? A mech's Full Tech: yes (Core p.71); an NPC's: no (p.280). Barrage: no, two distinct mounts. */
  repeat: boolean;
  /** The confirm row's label once both picks are in. */
  confirmLabel: string;
  /** An NPC's Barrage picks weapons (each is its own mount): the confirm row's "Pick N more weapons". */
  noun?: "weapon";
  diamonds: number;
};

/**
 * A pick screen's picks after choosing `rowId`. Without `repeat` a row toggles, and a third pick
 * drops the oldest. With `repeat` (Full Tech, Core p.71) a choice adds another copy while there is
 * room, so a row goes unpicked → once → twice; choosing a row already picked when the picks are
 * full clears that row's picks.
 */
export function nextPicks(pick: CommandMenuPick, picks: readonly string[], rowId: string): string[] {
  const has = picks.includes(rowId);
  if (!pick.repeat) return has ? picks.filter((p) => p !== rowId) : [...picks, rowId].slice(-pick.count);
  if (has && picks.length >= pick.count) return picks.filter((p) => p !== rowId);
  return [...picks, rowId].slice(-pick.count);
}

export type CommandMenuScreen = {
  id: CommandMenuScreenId;
  /** Breadcrumb title; empty at the root. */
  title: string;
  parent?: CommandMenuScreenId;
  rows: CommandMenuRow[];
  pick?: CommandMenuPick;
  /** The fire card's model, on the `fire` screen only. */
  fire?: CommandMenuFire;
  /** A hosted chooser's card: no rows; the host renders the chooser's controls on it. */
  chooser?: true;
  /**
   * A form card (no rows; the host renders its controls): Boost's, while the unit's Boost carries
   * opt-in riders (the Rapid Burst Jump Jet's Fly) to pick before it is taken. Unlike a chooser it
   * arms no aim, so it is in the tree whenever its row is.
   */
  form?: true;
  /**
   * The Full Tech card while the unit's Full Tech lock is open (3.3): the picks fired so far, off
   * the server-synced lock. The card then lists the options as direct picks (no pick-two toggles),
   * locks Back, and carries END FULL TECH.
   */
  fullTech?: { fired: string[]; count: 2 };
};

export type CommandMenuTree = {
  /**
   * Every screen; the fire card only while a Skirmish or Barrage is committed or its lock is open,
   * and a chooser's card only while that chooser is armed.
   */
  screens: Record<Exclude<CommandMenuScreenId, "fire" | "boost" | "reload" | CommandMenuChooserId>, CommandMenuScreen> & { fire?: CommandMenuScreen; boost?: CommandMenuScreen; reload?: CommandMenuScreen } & Partial<Record<CommandMenuChooserId, CommandMenuScreen>>;
  /** Diamonds left this turn (0 when the unit's turn is not open and cannot be). */
  diamondsLeft: number;
  /** Move spaces left this turn, off `TurnBudget.move` (R5); absent when the turn is not open. */
  moveLeft?: number;
  /**
   * The header's "(+N ◆)" (v20): the move a Boost would add (the unit's Speed), present only while
   * the Boost row is available (an action left, Boost unused this turn).
   */
  boostMove?: number;
  /** The legend's count: Protocols still available before the unit has acted. 0 hides the legend. */
  protocolLegendCount: number;
  /**
   * The unit is acting and has moved or acted this turn, so its Protocol window is closed (Decision
   * owed 1 of `projects/command-menu-sections.md`): the group cards collapse their Start of turn
   * section to {@link START_OF_TURN_CLOSED}. Read off the live state, never the pre-activation fold,
   * so a `ready` unit (whose rows read a hypothetical fresh turn) is never collapsed.
   */
  protocolWindowClosed: boolean;
  /**
   * END TURN shows: the unit is acting, or it is READY and its first action would open its turn
   * (`turn-presence` Decision 2 A, which reverses R7 for READY units). On a READY unit the press
   * passes the turn (`activate_unit → end_turn`).
   */
  endTurn: boolean;
  /** Ending the turn now would forfeit an action ({@link actionsLeftToEnd}), so END TURN confirms first. */
  actionsLeft: boolean;
  /** The diamonds and the MOVE / Boost counters show: the unit's turn is open or its first action opens it (R7). */
  counters: boolean;
  /** The one line under the header that holds for the whole unit (`unitWall`), absent while nothing does. */
  unitLine?: string;
  /**
   * A compact menu (F1, F3): `done` has no rows at all (the unit took its turn); `wall` holds, flat
   * on the root, only the rows a Stunned, Shut Down or holding unit can still take.
   */
  compact?: "done" | "wall";
  /** The unit's reaction, while it is not acting (F2): {@link reactionLine}. */
  reaction?: { open: boolean; names: string[] };
};

/**
 * The card an open, server-synced action lives on, or `null` (3.3): the fire card while a Skirmish
 * or Barrage is committed or its lock is open, the live Full Tech card while a Full Tech lock is
 * open. A menu mounted fresh (reopened, a reload, a second client) opens on it, and it is where the
 * menu stays until the lock closes.
 */
export function liveScreenOf(tree: CommandMenuTree): "fire" | "full_tech" | null {
  if (tree.screens.fire) return "fire";
  if (tree.screens.full_tech?.fullTech) return "full_tech";
  return null;
}

/** The grapple follow-up rows' ids (3.3), the root's top row while a grapple is held. */
export type GrappleRowId = "grapple_release" | "grapple_break" | "grapple_contest";

/** The grapple follow-up rows' copy (`rulesCopyRegistry`'s `command-menu-rows`; Core p.69, p.72). */
export const GRAPPLE_ROW_LABEL: Readonly<Record<GrappleRowId, string>> = {
  grapple_release: "Release Grapple",
  grapple_break: "Break Grapple",
  grapple_contest: "Contest Grapple",
};

/** The grapple follow-up row's rules blurb, naming the partner (the sheet's button titles). */
export function grappleRowBlurb(id: GrappleRowId, partner: string): string {
  if (id === "grapple_release") return `End the grapple with ${partner} unconditionally (free action).`;
  if (id === "grapple_break") return `A contested HULL check to break free of ${partner} (Quick action; loses on a tie).`;
  return `Same Size as ${partner}: a contested HULL check at the start of your turn; the winner counts as larger until the contest is repeated.`;
}

/** The grapple follow-up row's detail line. */
export function grappleRowDetail(partner: string): string {
  return `Grappled with ${partner}`;
}

/** The live Full Tech card's copy (3.3). */
export const FULL_TECH_END_LABEL = "END FULL TECH";
export function fullTechProgress(fired: number): string {
  return `${fired} of 2 fired · pick an option, then click a target`;
}

/** The fire card's progress line: how many weapons have fired and how many can still fire. */
export function fireProgress(fire: Pick<CommandMenuFire, "fired" | "fireable">): string {
  return `${fire.fired.length} FIRED · ${fire.fireable.length} LEFT`;
}

const GROUPS: readonly { id: CommandMenuGroupId; title: string }[] = [
  { id: "attack", title: "Attack" },
  { id: "hack", title: "Hack" },
  { id: "maneuver", title: "Maneuver" },
  { id: "equipment", title: "Use Equipment" },
  { id: "system", title: "Mech System" },
];

/**
 * An NPC's two relabelled slots (`npc-command-menu` Q1): Features holds every `npc_feature`
 * activation, System holds Stabilize. The slots and their order are the mech's.
 */
const NPC_GROUP_TITLE: Readonly<Partial<Record<CommandMenuGroupId, string>>> = {
  equipment: "Features",
  system: "System",
};

/**
 * A pilot's two relabelled slots (`pilot-command-menu` Q1): Gear holds every pilot-gear activation,
 * Mech holds Mount, Ride and Dismount (ally). It has no Hack slot ({@link groupsOf}).
 */
const PILOT_GROUP_TITLE: Readonly<Partial<Record<CommandMenuGroupId, string>>> = {
  equipment: "Gear",
  system: "Mech",
};

/**
 * The unit's groups, in menu order: a pilot drops Hack in place (RAW p.74 gives pilots no tech
 * actions; Q1 rejected a Hack slot that is always grey). A mech or NPC keeps all five, even one
 * whose group is empty (the rig's Attack, which it cannot use).
 */
function groupsOf(c: Combatant): readonly (typeof GROUPS)[number][] {
  return c.kind === "pilot" ? GROUPS.filter((g) => g.id !== "hack") : GROUPS;
}

function groupTitle(c: Combatant, g: (typeof GROUPS)[number]): string {
  const relabel = c.kind === "npc" ? NPC_GROUP_TITLE : c.kind === "pilot" ? PILOT_GROUP_TITLE : undefined;
  return relabel?.[g.id] ?? g.title;
}

/**
 * Core action id → group, in each group's row order (the prototype's). Ids absent here are not on
 * the menu: the two reactions (Overwatch, Brace) stay on the sheet's REACTION column, and a pilot's
 * engine-pending Activate and Skill Check are left off, as the mech menu leaves them (Q9). A pilot
 * shares no id with a mech, so its rows sit among the mech's in one table.
 */
const CORE_GROUP: readonly (readonly [string, CommandMenuGroupId])[] = [
  ["skirmish", "attack"],
  ["barrage", "attack"],
  ["ram", "attack"],
  ["grapple", "attack"],
  ["improvised_attack", "attack"],
  ["fight", "attack"],
  ["jockey", "attack"],
  ["reload", "attack"],
  ["quick_tech", "hack"],
  ["full_tech", "hack"],
  ["boost", "maneuver"],
  ["hide", "maneuver"],
  ["search", "maneuver"],
  ["prepare", "maneuver"],
  ["disengage", "maneuver"],
  ["engage_compcon", "equipment"],
  ["take_back_compcon", "equipment"],
  ["stabilize", "system"],
  ["overcharge", "system"],
  ["shut_down", "system"],
  ["boot_up", "system"],
  ["dismount", "system"],
  ["self_destruct", "system"],
  ["eject", "system"],
  ["mount", "system"],
  ["ride", "system"],
  ["ride_dismount", "system"],
];

/** The core rows that drill into a child screen. */
const CORE_CHILD: Readonly<Record<string, CommandMenuScreenId>> = {
  skirmish: "skirmish",
  fight: "skirmish",
  barrage: "barrage",
  quick_tech: "quick_tech",
  full_tech: "full_tech",
};

/** Overcharge's escalating self-heat by step (Core p.73) — the row states what it costs. */
const OVERCHARGE_HEAT = ["1", "1d3", "1d6", "1d6+4"] as const;

/**
 * Overcharge's row detail at escalation `step` (0 = never overcharged; Core p.73: 1, then 1d3, then
 * 1d6, then 1d6+4 heat each time after). Exported as copy data for `rulesCopyRegistry.ts`.
 */
export function overchargeRowDetail(step: number): string {
  return `Take ${OVERCHARGE_HEAT[Math.min(Math.max(step, 0), 3)]} heat for one more Quick action`;
}

/**
 * A ranged-or-melee weapon's line (`WeaponProfile.eitherMode`, the Ram Cannon; Core p.297). With no
 * mode used this turn it states both profiles: the engine swings melee at a target within Threat
 * and fires ranged beyond it. Once the turn's latch holds a mode, it states that mode, since the
 * other is refused until the next turn.
 */
export function weaponModeLine(args: { weapon: WeaponProfile; latched: WeaponMode | undefined }): string {
  const { weapon: w, latched } = args;
  if (latched === "melee") return "Melee this turn";
  if (latched === "ranged") return "Ranged this turn";
  const range = w.range ?? [];
  const reach = Math.max(0, ...range.filter((r) => r.kind === "range").map((r) => r.val));
  return `Melee within Threat ${threatValue(range)} · ranged to Range ${reach}`;
}

/** A discharged Recharge item's line, a weapon's or a feature's (Core p.281). */
export function dischargedLine(on: number): string {
  return `Discharged · recharges on a ${on}+`;
}

/** A counted allowance's line: "2 of 3 left". */
export function usesLeftLine(uses: { left: number; max: number }): string {
  return `${uses.left} of ${uses.max} left`;
}

/** A feature row's reason once its round cap is spent. */
export const USED_THIS_ROUND = "Used this round";

/** Invade's reason on a unit that cannot make attacks (the rig's Unarmed, `cannotAttack`). */
export const CANT_MAKE_ATTACKS = "Can't make attacks";

/** An NPC's live Full Tech row for the option it already fired (Core p.280: two different Quick Techs). */
export const FULL_TECH_REPEAT = "Already used this Full Tech";

/** A group with no rows (R5): the one wording, on its row and on its empty card. */
export const NOTHING_TO_USE = "Nothing to use";

/**
 * The unit lines a status states (R2; Core p.78, p.77): what the header's turn word can't say.
 * The engine's own sentences stay on the rejection toasts.
 */
export const UNIT_LINE = {
  stunned: "Stunned · no actions until it clears",
  shutDown: "Shut Down · Boot Up to act",
  downAndOut: "Down and Out",
  rodeo: "In a Black Thumb Rodeo · the mech idles",
} as const;

/** The unit line while the unit holds a prepared action (R2). */
export function heldLine(actionName: string): string {
  return `Holding a prepared ${actionName}`;
}

/** The reaction line under a menu whose unit is not acting (F2), in the unit sheet's OPEN / SPENT words. */
export function reactionLine(reaction: { open: boolean; names: readonly string[] }): string {
  if (!reaction.open) return "REACTION SPENT";
  return reaction.names.length > 0 ? `REACTION OPEN · ${reaction.names.join(", ")}` : "REACTION OPEN";
}

/**
 * The action id an NPC's Stabilize row carries: its choice is fixed, so the row fires on the click
 * instead of arming the mech's chooser (`stabilize`). The row's own id stays `stabilize`.
 */
export const NPC_STABILIZE_ROW = "npc_stabilize";

/** NPC Stabilize's row detail (Core p.280): its one fixed choice, so the row fires on the click. */
export const NPC_STABILIZE_DETAIL = "Clear all heat and Exposed, reload all Loading weapons";

/**
 * The short, on-screen form of a blocked weapon's reason (the prototype's "Unloaded · Stabilize to
 * reload"); {@link weaponCardTitle} is the full sentence the weapon card and the row's title carry.
 * Exported as copy data for `rulesCopyRegistry.ts`, since each line states a rule.
 */
export function weaponBlockShort(args: { weapon: WeaponProfile; combatant: Combatant; state: WeaponCardState }): string | undefined {
  const { weapon: w, combatant: c, state } = args;
  switch (state) {
    case "destroyed":
      return "Destroyed";
    case "discharged":
      return dischargedLine(w.recharge ?? 0);
    case "unloaded":
      return `Unloaded · ${c.kind === "pilot" ? "Reload" : "Stabilize"} to reload`;
    case "limited":
      return "Out of charges";
    case "ordnance-window":
      return "Ordnance · fire before moving or acting";
    case "ordnance-overwatch":
      return "Ordnance · never on Overwatch";
    case "superheavy-skirmish":
    case "superheavy-overwatch":
      return "Superheavy · Barrage only";
    case "armable":
      return undefined;
  }
}

/** An economy cost string as the action {@link bracedTurnLimit} weighs; `undefined` for a Reaction, which Braced walls elsewhere. */
function costAction(cost: string): "quick" | "full" | "free" | undefined {
  const c = cost.trim().toLowerCase();
  if (c.startsWith("full")) return "full";
  if (c.startsWith("quick")) return "quick";
  if (c.startsWith("free") || c.startsWith("protocol")) return "free";
  return undefined;
}

/** A braced-turn row's reason, per {@link BracedTurnLimit}. */
const BRACED_ROW_REASON: Record<BracedTurnLimit, string> = {
  no_full: "Braced: no Full actions this turn",
  no_free: "Braced: no free actions this turn",
  quick_taken: "Braced: its one Quick action is taken",
};

/** Diamonds for an economy cost string: Quick 1, Full 2, anything else (Free, Protocol) 0. */
function diamondsFor(cost: string): number {
  const c = cost.trim().toLowerCase();
  if (c.startsWith("full")) return 2;
  if (c.startsWith("quick")) return 1;
  return 0;
}

/**
 * An availability verdict: `undefined` reason ⇒ available. `cantAfford` marks a verdict that is
 * only the diamond budget (the "can't-afford rows say so only through their diamonds" ruling);
 * `blockedBy` one that shows no line ({@link CommandMenuRow.blockedBy}).
 */
type Verdict = { reason?: string; cantAfford?: true; blockedBy?: "unit" | "children" };

/** A reason the row shows as its own line: not the unit wall's, not the budget's. */
function ownReason(v: Verdict): boolean {
  return v.reason !== undefined && v.blockedBy === undefined && !v.cantAfford;
}

/**
 * The row's verdict under the unit wall (R1): its own reason when it has one, since that is what
 * will still matter once the wall lifts, else the wall, else what is left (the budget, or nothing).
 */
function gate(wall: Verdict | undefined, own: Verdict): Verdict {
  return ownReason(own) || !wall ? own : wall;
}

/** A row's verdict as it stands (the parent verdict a drilled row's children inherit). */
function verdictOf(r: CommandMenuRow): Verdict {
  return { ...(r.reason !== undefined ? { reason: r.reason } : {}), ...(r.cantAfford ? { cantAfford: true as const } : {}), ...(r.blockedBy ? { blockedBy: r.blockedBy } : {}) };
}

/**
 * The diamond budget's verdict. Its reason is the row's short accessible label only: the row shows
 * no line or tooltip for it, and says so through its diamonds (held filled, missing hollow).
 */
function budgetVerdict(diamonds: number, left: number): Verdict {
  if (diamonds <= left) return {};
  return { reason: left === 0 ? "No actions left this turn" : `Needs ${diamonds} actions, ${left} left`, cantAfford: true };
}

/**
 * Which group a granted row belongs to (R6): a grant off a mech **system or weapon** is Use
 * Equipment; everything else — frame traits and core power (Initiative, Power Up), talents, core
 * bonuses — is Mech System. The item id is the part before the last `:` on the granted class
 * (`ms_type_3_projected_shield:0`) and the bare id on the effect class (`everest_initiative`).
 */
function grantedGroup(model: UnitActionData, c: Combatant, v: UnitActionView): CommandMenuGroupId {
  // An NPC's granted rows are all `npc_feature` activations: Features (Q1). A pilot's are all
  // pilot-gear activations: Gear (`pilot-command-menu` Q1).
  if (c.kind === "npc" || c.kind === "pilot") return "equipment";
  const colon = v.id.lastIndexOf(":");
  const itemId = colon >= 0 ? v.id.slice(0, colon) : v.id;
  const isEquipment = model.compendium.getById("system", itemId) !== undefined || model.compendium.getById("weapon", itemId) !== undefined;
  return isEquipment ? "equipment" : "system";
}

/** The frame's core power name ("Power Up"), which R6 puts first under Mech System. */
function corePowerName(model: UnitActionData, c: Combatant): string | undefined {
  const frame = c.sourceRef ? (model.compendium.getById("frame", c.sourceRef) as Record<string, unknown> | undefined) : undefined;
  const cs = frame?.["core_system"] as { active_name?: unknown; name?: unknown } | undefined;
  if (typeof cs?.active_name === "string" && cs.active_name.trim()) return cs.active_name;
  return typeof cs?.name === "string" ? cs.name : undefined;
}

/**
 * The row's own reason, without the unit wall or the budget (R1, R3). Precedence follows the room's
 * gate ladder: the once-per-turn ledger, the Protocol window, the row-specific walls. A turn-scoped
 * reason ("Already used this turn", "Start of turn only", "Already Engaged") holds only while the
 * turn is open (`live`); a lasting one (Destroyed, Discharged, uses, the round cap, engine-pending)
 * shows off-turn too, since it will still matter next turn.
 */
function rowVerdict(args: { model: UnitActionData; combatant: Combatant; view: UnitActionView; feature?: FeatureState }): Verdict {
  const { model, combatant: c, view: v, feature } = args;
  const { economy } = model;
  const live = economy.economyLive;
  const backswing = v.id === "skirmish" && model.backswingWeaponId !== undefined;
  if (v.granted) {
    // A round cap or a spent count lasts past the turn; a granted row's `usedUp` also covers them
    // (Power Up is 1/scene), so it claims "this turn" only while its count still has uses left.
    // An empty pool outlasts the round, so it reads first; a spent round rate beside a pool with
    // charges left reads "Used this round" (`decisions/0202`: Drop Bomb).
    if (feature?.uses?.per === "scene" && feature.uses.left === 0) return { reason: "No uses remaining" };
    if (feature?.usedThisRound) return { reason: USED_THIS_ROUND };
    if (live && v.usedUp) return { reason: "Already used this turn" };
  } else if (live && v.usedUp && !backswing) {
    return { reason: v.id === "engage_compcon" && model.compConEngaged ? "Already Engaged" : "Already used this turn" };
  }
  // Brace's next turn (RAW p.73): the same reading the server's `checkBudget` refuses with.
  const action = costAction(v.cost);
  const braced = live && action !== undefined ? bracedTurnLimit(c, economy.state.activation, action) : undefined;
  if (braced !== undefined) return { reason: BRACED_ROW_REASON[braced] };
  if (live && isProtocolCost(v.cost) && !isTurnVirgin(economy.state, c.id)) return { reason: "Start of turn only" };
  if (live && v.id === "take_back_compcon" && c.statuses.find((st) => st.name === COMPCON_ENGAGED_STATUS)?.appliedTurn === economy.state.activation.turnOrdinal) {
    return { reason: "From your next turn" };
  }
  if (v.id === "boot_up" && !hasStatus(c.statuses, SHUT_DOWN_STATUS)) return { reason: "Only while shut down" };
  if (v.granted) {
    // The item's own state, as `checkGrantedActionLegality` walls it: destroyed, or discharged.
    if (feature?.destroyed) return { reason: "Destroyed" };
    if (feature?.recharge && !feature.recharge.charged) return { reason: dischargedLine(feature.recharge.on) };
    if (!v.resolvable) return { reason: "GM adjudicates — no engine body yet" };
    if (v.conditionUnmet) return { reason: "Not available yet — its trigger condition isn't met" };
    if (!grantedTargetingFireable(v.targeting)) return { reason: "Not yet fireable from the browser" };
  } else if (!v.live) {
    return { reason: "Resolves manually (engine pending)" };
  }
  return {};
}

/**
 * A parent's verdict once its children are known: available only if one child is (R5). It shows a
 * line only when every child is greyed by the same reason of its own; children greyed only by the
 * wall, only by the budget, or for different reasons leave it greyed and silent. Display only (the
 * 2026-09-24 "always explorable" ruling): the menu opens a parent's screen whatever it says.
 */
function withChildren(own: Verdict, children: readonly CommandMenuRow[]): Verdict {
  if (own.reason) return own;
  if (children.length === 0) return { reason: NOTHING_TO_USE };
  if (children.some((r) => r.available)) return {};
  const first = verdictOf(children[0]!);
  if (children.every((r) => r.reason === first.reason && r.blockedBy === first.blockedBy && !r.cantAfford === !first.cantAfford)) return first;
  if (children.every((r) => r.cantAfford)) return { reason: first.reason!, cantAfford: true };
  return { reason: NOTHING_AVAILABLE, blockedBy: "children" };
}

/** A drilling row's accessible label when its children are greyed for different reasons (it shows no line). */
const NOTHING_AVAILABLE = "Nothing available right now";

function row(args: {
  id: string;
  kind: CommandMenuRow["kind"];
  label: string;
  diamonds: number;
  protocol?: boolean;
  verdict: Verdict;
  usedThisTurn?: boolean;
  protocolBelow?: boolean;
  extra?: Partial<CommandMenuRow>;
}): CommandMenuRow {
  const protocol = args.protocol ?? false;
  return {
    id: args.id,
    kind: args.kind,
    label: args.label,
    diamonds: args.diamonds,
    protocol,
    free: args.kind === "action" && args.diamonds === 0 && !protocol && args.extra?.child === undefined,
    available: args.verdict.reason === undefined,
    ...(args.verdict.reason !== undefined ? { reason: args.verdict.reason } : {}),
    ...(args.verdict.cantAfford ? { cantAfford: true as const } : {}),
    ...(args.verdict.blockedBy ? { blockedBy: args.verdict.blockedBy } : {}),
    usedThisTurn: args.usedThisTurn ?? false,
    protocolBelow: args.protocolBelow ?? false,
    ...args.extra,
  };
}

/**
 * An NPC weapon's size and class line (`"Main Rifle"`), off its compendium feature's `weapon_type`:
 * an NPC weapon is its own mount, so this is its row's secondary. The frozen profile keeps only the
 * size (`WeaponProfile.mount`), which is the fallback.
 */
export function npcWeaponLine(compendium: Pick<Compendium, "getById">, w: WeaponProfile): string {
  const feature = compendium.getById("npc_feature", w.id) as { weapon_type?: unknown } | undefined;
  return typeof feature?.weapon_type === "string" && feature.weapon_type.trim() ? feature.weapon_type.trim() : (w.mount ?? "Weapon");
}

/** Tags every pilot weapon carries or that say nothing about firing it: left off {@link pilotWeaponLine}. */
const PILOT_LINE_OMITS: ReadonlySet<string> = new Set(["tg_pilot_weapon", "tg_set_damage_type"]);

/**
 * A pilot weapon's line (`pilot-command-menu` Q4): its range and the tags that change how it fires
 * ("Range 3 · Sidearm"). A pilot weapon is its own mount, so this is its Fight row's secondary.
 */
export function pilotWeaponLine(w: WeaponProfile): string {
  const tags = (w.tags ?? []).filter((t) => !PILOT_LINE_OMITS.has(t.id)).map((t) => titleCase(formatTagChip(t.id, t.val)));
  return [w.range && w.range.length > 0 ? formatWeaponRange(w.range) : "", ...tags].filter((p) => p !== "").join(" · ");
}

function titleCase(chip: string): string {
  return chip.toLowerCase().replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
}

/** The mounts a Skirmish or Barrage picks from, grouped by {@link mountKeyOf}, destroyed weapons included (greyed). */
function mountGroups(c: Combatant, compendium: Pick<Compendium, "getById">): { key: string; name: string; weapons: WeaponProfile[] }[] {
  const groups = new Map<string, { key: string; name: string; weapons: WeaponProfile[] }>();
  for (const w of c.base.weapons ?? []) {
    const key = mountKeyOf(c.base.mounts, weaponInstanceKey(w));
    const idx = key.startsWith("mount:") ? Number(key.slice("mount:".length)) : -1;
    const mountType = idx >= 0 ? c.base.mounts?.[idx]?.type : undefined;
    const existing = groups.get(key);
    if (existing) existing.weapons.push(w);
    else groups.set(key, { key, name: c.kind === "npc" ? npcWeaponLine(compendium, w) : c.kind === "pilot" ? pilotWeaponLine(w) : mountType ? `${mountType} mount` : "Integrated", weapons: [w] });
  }
  return [...groups.values()];
}

/** Tags lancer-data lists without a value that the engine reads as 1 (an unvalued Accurate is +1). */
const VAL_DEFAULTS_TO_ONE: ReadonlySet<string> = new Set(["tg_accurate", "tg_inaccurate"]);

/**
 * A tag's rule text for its chip's hover: the compendium tag entry's `description` (`lancer-data`
 * `tags.json`, per `decisions/0122`), `{VAL}` filled with the weapon's value (1 for an unvalued
 * Accurate or Inaccurate, as the engine reads it; `X` for any other unvalued tag). `undefined` for
 * a tag the compendium does not know.
 */
export function tagRuleText(compendium: Pick<Compendium, "getById">, id: string, val?: number): string | undefined {
  const entry = compendium.getById("tag", id) as { description?: unknown } | undefined;
  const text = typeof entry?.description === "string" ? entry.description.trim() : "";
  if (!text) return undefined;
  const v = val ?? (VAL_DEFAULTS_TO_ONE.has(id) ? 1 : undefined);
  return text.replaceAll("{VAL}", v !== undefined ? String(v) : "X");
}

function mountWeapon(args: { weapon: WeaponProfile; combatant: Combatant; state: EncounterState; compendium: Pick<Compendium, "getById">; barrage: boolean }): CommandMenuWeapon {
  const { weapon: w, combatant: c, state, compendium, barrage } = args;
  const verdict = classifySkirmishCard({ weapon: w, combatant: c, state, isOverwatch: false, barrage });
  const reason = weaponCardTitle({ weapon: w, combatant: c, state: verdict });
  const reasonShort = weaponBlockShort({ weapon: w, combatant: c, state: verdict });
  const latched = w.eitherMode ? latchedWeaponMode({ resources: c.current.resources, instanceKey: weaponInstanceKey(w), turnOrdinal: state.activation.turnOrdinal }) : undefined;
  return {
    key: weaponInstanceKey(w),
    name: w.name,
    range: w.range && w.range.length > 0 ? formatWeaponRange(w.range) : "",
    damage: formatWeaponDamage(w.damage),
    tags: (w.tags ?? []).map((t) => {
      const rule = tagRuleText(compendium, t.id, t.val);
      return { id: t.id, label: formatTagChip(t.id, t.val), ...(t.val !== undefined ? { val: t.val } : {}), ...(rule !== undefined ? { rule } : {}) };
    }),
    state: verdict,
    ...(reason !== undefined ? { reason } : {}),
    ...(reasonShort !== undefined ? { reasonShort } : {}),
    ...(w.eitherMode ? { mode: weaponModeLine({ weapon: w, latched }) } : {}),
  };
}

/**
 * The mount rows under Skirmish (`diamonds` 1) or Barrage (a toggle, 0). A mount is pickable when
 * one of its weapons can fire (R7); a mount whose every weapon is blocked takes its first weapon's
 * reason. The parent's own verdict, when it blocks, is every mount's reason.
 */
/**
 * The Skirmish card's one row for a mech with no weapons at all: the engine's unarmed strike (a
 * `request_attack` naming no weapon resolves `FALLBACK_WEAPON`, 1d6 kinetic), the same shot the
 * retired sheet chooser's weaponless state fired. Its aim is a Skirmish with no weapon.
 */
export const UNARMED_STRIKE_ROW = "unarmed_strike";

/** The unarmed strike's damage line, off the engine's own fallback profile. */
export function unarmedStrikeDetail(): string {
  return formatWeaponDamage(FALLBACK_WEAPON.damage);
}

function unarmedStrikeRow(args: { parent: Verdict; diamonds: number }): CommandMenuRow {
  return row({ id: UNARMED_STRIKE_ROW, kind: "action", label: FALLBACK_WEAPON.name, diamonds: args.diamonds, verdict: args.parent, extra: { actionId: UNARMED_STRIKE_ROW, detail: unarmedStrikeDetail() } });
}

function mountRows(args: { combatant: Combatant; state: EncounterState; parent: Verdict; diamonds: number; compendium: Pick<Compendium, "getById">; barrage: boolean; reserved?: string | undefined }): CommandMenuRow[] {
  const { combatant: c, state, parent, diamonds, compendium, barrage, reserved } = args;
  const reservedMount = reserved !== undefined ? mountKeyOf(c.base.mounts, reserved) : undefined;
  return mountGroups(c, compendium).map((m) => {
    const weapons = m.weapons.map((w) => mountWeapon({ weapon: w, combatant: c, state, compendium, barrage }));
    const inReach = c.pos ? Math.max(0, ...weapons.map((w) => targetsInReach(state, c.id, w.key).length)) : undefined;
    const firstBlocked = weapons.find((w) => w.state !== "armable");
    // A live Backswing Cut reservation: only the reserved weapon's mount can take the follow-up
    // (its aim pins that weapon), and it fires whatever the card's own blocks say.
    const own: Verdict =
      reservedMount !== undefined
        ? m.key === reservedMount
          ? {}
          : { reason: backswingOnlyReason(c, reserved!) }
        : weapons.some((w) => w.state === "armable")
          ? {}
          : { reason: firstBlocked?.reason ?? "Nothing on this mount can fire" };
    // The parent's own reason holds for every mount; its wall or budget yields to the mount's own
    // reason (off-turn an Unloaded mount says so, not the unit line).
    const verdict = ownReason(parent) ? parent : gate(parent.reason !== undefined ? parent : undefined, own);
    return row({
      id: m.key,
      kind: "mount",
      label: weapons.map((w) => w.name).join(" + "),
      diamonds,
      verdict,
      extra: {
        actionId: m.key,
        secondary: m.name,
        weapons,
        superheavy: m.weapons.some((w) => w.mount === WEAPON_SIZE_SUPERHEAVY),
        ...(inReach !== undefined ? { inReach } : {}),
        ...(verdict === own && reservedMount === undefined && own.reason !== undefined && firstBlocked?.reasonShort ? { reasonShort: firstBlocked.reasonShort } : {}),
      },
    });
  });
}

/** Why a weapon (or its mount) can't take a live Backswing Cut follow-up: the reservation names one weapon. */
export function backswingOnlyReason(c: Combatant, reserved: string): string {
  const name = (c.base.weapons ?? []).find((w) => weaponInstanceKey(w) === reserved)?.name ?? "the reserved weapon";
  return `Backswing Cut: only ${name} can follow up`;
}

/** The fire card's rule-stating row copy (`rulesCopyRegistry`'s `command-menu-rows`). */
export const FIRE_RIDER_NOTE = "Aux rider · no bonus damage";
/** A Main fired after its mount's Aux: the Main is the primary, and the Aux already fired becomes the rider (Core p.70/p.71). */
export const FIRE_PAIRS_RIDER_NOTE = "Primary · the Aux fired on this mount becomes its rider";
export const FIRE_FIRED = "Fired";
export const FIRE_PAIR_SHORT = "Its mount already fired its pair";
/** A weapon that attacks several targets at a time (its entry's `attacksTargets`): how many one shot may pick. */
export function fireTargetsNote(cap: number): string {
  return `Up to ${cap} targets`;
}
/** A multi-attack weapon's next attack ("can make three attacks at a time"): which one of how many. */
export function fireAttackNote(next: number, of: number): string {
  return `Attack ${next} of ${of}`;
}

/**
 * The fire card (R7; the prototype's `bfire`): the committed mounts of a Skirmish or Barrage as
 * headings, each over its weapons. It reads the **server-synced lock** when one is open for this
 * unit (its mounts, the fired weapons, its Ordnance snapshot), else the menu's local
 * {@link FireCommit} (the first shot has not been sent). Each weapon is fired, blocked (its weapon
 * state via {@link weaponFireBlock}, then the lock's pairing via {@link lockPairingBlock}, so a
 * greyed reason matches the room's rejection), or fireable; a fireable Aux that would be its
 * mount's rider says so ({@link isRiderShot}), and a fireable Main whose mount's Aux already fired
 * says that Aux becomes the rider ({@link pairedAuxRiderOf}). `undefined` when there is nothing to fire.
 */
function fireScreen(args: {
  combatant: Combatant;
  state: EncounterState;
  lock: AttackLock | null;
  commit: FireCommit | null;
  skirmishUsable: boolean;
  compendium: Pick<Compendium, "getById">;
  /** A live Backswing Cut reservation's weapon: before the follow-up's shot only it can fire. */
  reserved?: string | undefined;
  /** The loaded overlay, for a weapon's `attacksTargets`. */
  overlay?: LoadedOverlay | undefined;
}): CommandMenuScreen | undefined {
  const { combatant: c, state, commit, skirmishUsable, compendium } = args;
  const lock = args.lock && (args.lock.action === "skirmish" || args.lock.action === "barrage") ? args.lock : null;
  const action: FireAction | undefined = lock ? (lock.action as FireAction) : commit?.action;
  if (!action) return undefined;
  const fired = lock?.weapons ?? [];
  const mounts = lock ? (lock.mounts ?? [...new Set(fired.map((f) => mountKeyOf(c.base.mounts, f)))]) : (commit?.mounts ?? []);
  const ordnanceWindow = lock ? lock.ordnanceWindow === true : checkOrdnanceWindow(state, c.id);
  const all = mountGroups(c, compendium);
  const rows: CommandMenuRow[] = [];
  const groups: CommandMenuFire["groups"] = [];
  const fireable: string[] = [];
  for (const key of mounts) {
    const g = all.find((m) => m.key === key);
    if (!g) continue;
    const group = { key, name: g.name, weaponKeys: [] as string[] };
    groups.push(group);
    for (const w of g.weapons) {
      const weapon = mountWeapon({ weapon: w, combatant: c, state, compendium, barrage: action === "barrage" });
      const k = weapon.key;
      group.weaponKeys.push(k);
      // A multi-attack weapon is spent only once it has made every attack (`weaponAttackCount`).
      const attacksMade = fired.filter((f) => f === k).length;
      const attackCount = weaponAttackCount(w);
      const targetCap = attacksTargets(args.overlay, w.id) ?? 1;
      const isFired = attacksMade >= attackCount;
      const stateBlock = isItemDestroyed(c.current.resources, k) ? "Destroyed" : weaponFireBlock({ state, attackerId: c.id, weapon: k, ordnanceWindow });
      const pairBlock = stateBlock === undefined && lock ? lockPairingBlock({ state, attackerId: c.id, weapon: k, lock }) : undefined;
      const reservedBlock = !lock && action === "skirmish" && args.reserved !== undefined && k !== args.reserved ? backswingOnlyReason(c, args.reserved) : undefined;
      const reason = isFired ? FIRE_FIRED : (stateBlock ?? pairBlock ?? reservedBlock);
      // The on-screen short form: the weapon card's own short reason where the weapon's state blocks
      // it (the lock's Ordnance snapshot can keep a weapon open the live card would close).
      const cardShort = weapon.state !== "armable" && !(weapon.state === "ordnance-window" && ordnanceWindow) ? weapon.reasonShort : undefined;
      const reasonShort = isFired ? FIRE_FIRED : stateBlock !== undefined ? (cardShort ?? stateBlock) : pairBlock !== undefined ? FIRE_PAIR_SHORT : reservedBlock;
      const available = reason === undefined;
      if (available) fireable.push(k);
      const riderArgs = { state, attackerId: c.id, fired, weaponKey: k };
      const note = !available
        ? undefined
        : isRiderShot(riderArgs)
          ? FIRE_RIDER_NOTE
          : pairedAuxRiderOf(riderArgs) !== undefined
            ? FIRE_PAIRS_RIDER_NOTE
            : attackCount > 1
              ? fireAttackNote(attacksMade + 1, attackCount)
              : targetCap > 1
                ? fireTargetsNote(targetCap)
                : undefined;
      rows.push(
        row({
          id: k,
          kind: "weapon",
          label: w.name,
          diamonds: 0,
          verdict: reason !== undefined ? { reason } : {},
          usedThisTurn: isFired,
          extra: {
            actionId: k,
            secondary: g.name,
            weapons: [weapon],
            attack: action,
            ...(reasonShort !== undefined ? { reasonShort } : {}),
            ...(note !== undefined ? { note } : {}),
          },
        }),
      );
    }
  }
  const firedMounts = new Set(fired.map((f) => mountKeyOf(c.base.mounts, f)));
  const exit: FireExit =
    fired.length === 0
      ? "cancel"
      : action === "skirmish"
        ? "stop_firing"
        : firedMounts.size < 2 && checkStopBarrageAsSkirmish({ state, attackerId: c.id, skirmishUsable }).ok
          ? "stop_as_skirmish"
          : "end_early";
  return {
    id: "fire",
    title: "Fire",
    parent: action,
    rows,
    fire: { action, groups, fired: [...fired], live: lock !== null, fireable, exit, backLocked: fired.length > 0 },
  };
}

/** The unit line under the GM override: what it lifts, so the rows it leaves greyed read as expected. */
export const OVERRIDE_UNIT_LINE = "GM override · rules off";

/**
 * The rows a GM override can't force (`decisions/0209`): Boost is a move-budget command, not an
 * intent, and Prepare, the pilot-bridge and identity verbs (Mount, Jockey and its menu, Ride,
 * Dismount, Eject, Reload, Shake Off) and a held action's Release/Drop are not forceable yet.
 * Everything else in the menu sends a forceable intent.
 */
const UNFORCEABLE_ROWS: ReadonlySet<string> = new Set([
  "boost",
  "prepare",
  "mount",
  "jockey",
  "jockey_distract",
  "jockey_shred",
  "jockey_damage",
  "shake_off",
  "ride",
  "ride_dismount",
  "dismount",
  "eject",
  "reload",
  "release",
  "drop_prepare",
]);

/** `row` with every verdict lifted: available, no reason, every weapon on it armable. */
function liftedRow(row: CommandMenuRow): CommandMenuRow {
  const { reason: _reason, cantAfford: _cantAfford, blockedBy: _blockedBy, reasonShort: _reasonShort, ...rest } = row;
  return {
    ...rest,
    available: true,
    ...(row.weapons ? { weapons: row.weapons.map(({ reason: _r, reasonShort: _s, ...w }) => ({ ...w, state: "armable" as const })) } : {}),
  };
}

/**
 * The GM override's lift (`decisions/0209`): every row that sends a forceable intent, on every
 * screen (the groups, the actions, Skirmish's and Barrage's mounts, the tech options, the fire
 * card's unfired weapons), is available. The {@link UNFORCEABLE_ROWS} keep their verdicts.
 */
function liftForceable(screens: CommandMenuTree["screens"]): void {
  for (const id of Object.keys(screens) as CommandMenuScreenId[]) {
    const screen = screens[id];
    if (!screen || id === "reload") continue;
    screens[id] = { ...screen, rows: screen.rows.map((r) => (UNFORCEABLE_ROWS.has(r.id) || (id === "fire" && r.usedThisTurn) ? r : liftedRow(r))) };
  }
  const fire = screens.fire;
  if (fire?.fire) screens.fire = { ...fire, fire: { ...fire.fire, fireable: fire.rows.filter((r) => r.available).map((r) => r.id) } };
}

/**
 * Is a pick screen's selection ready to confirm? Two picks; for Barrage mounts a Superheavy's
 * mount alone (it fires alone, Core p.71), or the one mount a unit has when it has only one.
 */
export function pickReady(screen: CommandMenuScreen, picks: readonly string[]): boolean {
  if (!screen.pick) return false;
  if (picks.length >= screen.pick.count) return true;
  if (screen.id !== "barrage" || picks.length !== 1) return false;
  const picked = screen.rows.find((r) => r.id === picks[0]);
  return picked?.superheavy === true || screen.rows.filter((r) => r.available).length === 1;
}

/**
 * A pick screen's selection after toggling `row` (Barrage mounts: a Superheavy's mount is picked
 * alone and fills both slots; picking another mount replaces it; otherwise the newest two). Any
 * other pick screen (Full Tech, which may repeat an option) is {@link nextPicks}.
 */
export function togglePick(screen: CommandMenuScreen, picks: readonly string[], row: CommandMenuRow): string[] {
  const count = screen.pick?.count ?? 2;
  if (screen.id !== "barrage") return screen.pick ? nextPicks(screen.pick, picks, row.id) : [...picks];
  if (picks.includes(row.id)) return picks.filter((p) => p !== row.id);
  if (row.superheavy) return [row.id];
  const kept = picks.filter((p) => !screen.rows.find((r) => r.id === p)?.superheavy);
  return [...kept, row.id].slice(-count);
}

/**
 * The Quick Tech options (an NPC's are Invade and Lock On, Core p.280), each inheriting its parent
 * row's verdict (they share its once-per-turn counter). Invade is a tech attack, so a unit that
 * cannot make attacks gets it greyed. After the built-ins, one "Invade ›" row per invade option the
 * unit carries: its id is the option's own (`techMenuPick` decodes it to an Invade with the option
 * pre-answered), and it takes Invade's verdict, since every option spends Invade's one use.
 */
function techOptionRows(args: { model: UnitActionData; combatant: Combatant; parent: Verdict; diamonds: number; fullTech?: boolean }): CommandMenuRow[] {
  const { model, combatant: c, parent, diamonds, fullTech = false } = args;
  const options = legalQuickTechOptions({ npcActor: c.kind === "npc" });
  const invadeVerdict = !ownReason(parent) && model.cannotAttack !== undefined ? { reason: CANT_MAKE_ATTACKS } : parent;
  const builtIn = options.map((o) => row({ id: o.id, kind: "option", label: o.name, diamonds, verdict: o.id === "invade" ? invadeVerdict : parent, extra: { actionId: o.id } }));
  const invade = options.some((o) => o.id === "invade") ? invadeOptionRows({ model, verdict: invadeVerdict, diamonds }) : [];
  return [...builtIn, ...invade, ...(fullTech ? carriedQuickTechRows({ model, verdict: parent, diamonds }) : [])];
}

/** One "Invade ›" row per invade option the unit carries, each with Invade's own verdict. */
function invadeOptionRows(args: { model: UnitActionData; verdict: Verdict; diamonds: number; continuation?: true }): CommandMenuRow[] {
  const { model, verdict, diamonds, continuation } = args;
  return model.invadeOptions.map((o) => row({ id: o.id, kind: "option", label: `Invade › ${o.label}`, diamonds, verdict, extra: { actionId: o.id, ...(continuation ? { continuation } : {}) } }));
}

/**
 * A Full Tech's picks beyond the four built-ins: each carried Quick Tech action (Crush Targeting,
 * `activation: "quick_tech"`), its own row greyed while it can't be taken (a discharged Recharge).
 * The Quick Tech card doesn't list them: each already has its own granted row.
 */
function carriedQuickTechRows(args: { model: UnitActionData; verdict: Verdict; diamonds: number; continuation?: true }): CommandMenuRow[] {
  const { model, verdict, diamonds, continuation } = args;
  return model.grantedViews
    .filter((v) => v.quickTech && v.actionId !== undefined)
    .map((v) => row({ id: v.actionId!, kind: "option", label: v.name, diamonds, verdict: !ownReason(verdict) && v.usedUp ? { reason: "No uses remaining" } : verdict, extra: { actionId: v.actionId!, ...(continuation ? { continuation } : {}) } }));
}

/**
 * The unit wall's verdict for row `id` (R1), or `undefined` where it does not block: every row
 * while the unit holds a prepared action or its turn isn't open, and for a Stunned, Shut Down or
 * Down and Out unit the rows the status wall refuses ({@link actionStatusBlockReason}, whose own
 * exceptions keep Boot Up, Dismount and Eject).
 */
type WallFor = (id: string) => Verdict | undefined;

/** A row's verdict with the budget priced in, only while the turn is open (R3: the budget is turn-scoped). */
function priced(model: UnitActionData, own: Verdict, diamonds: number): Verdict {
  return own.reason || !model.economy.economyLive ? own : budgetVerdict(diamonds, model.economy.ap);
}

/** The verdict of a row with no core-catalog descriptor (the grapple follow-up, the mine rows): the wall, then the budget. */
function offCatalogVerdict(args: { model: UnitActionData; id: string; diamonds: number; wallFor: WallFor }): Verdict {
  const { model, id, diamonds, wallFor } = args;
  return gate(wallFor(id), priced(model, {}, diamonds));
}

/** The mine rows' ids (Core p.106): any character's two Quick actions against MINES. */
export type MineRowId = "detect_mine" | "disarm_mine";

/** The mine rows' copy (`rulesCopyRegistry`'s `command-menu-rows`; Core p.106). */
export const MINE_ROW_LABEL: Readonly<Record<MineRowId, string>> = {
  detect_mine: "Detect Mines",
  disarm_mine: "Disarm Mine",
};

export const MINE_ROW_BLURB: Readonly<Record<MineRowId, string>> = {
  detect_mine: "A Systems check: on a success, every hidden mine within your Sensors is revealed.",
  disarm_mine: "A Systems check against an adjacent mine: on a success it is removed; on a failure it detonates.",
};

/**
 * Detect Mines and Disarm Mine, the Maneuver rows beside Search. Neither is a core-catalog action,
 * so each prices off the economy's own `intentCost` for its intent. Detect fires on the click;
 * Disarm hosts its chooser card, which lists the mines this unit may disarm, the way Search's lists
 * its targets. Neither row reads the board for mines, so its state says nothing about hidden ones.
 */
function mineRows(args: { model: UnitActionData; combatant: Combatant; wallFor: WallFor }): CommandMenuRow[] {
  const { model, combatant: c, wallFor } = args;
  const make = (id: MineRowId, points: number) =>
    row({ id, kind: "action", label: MINE_ROW_LABEL[id], diamonds: points, verdict: offCatalogVerdict({ model, id, diamonds: points, wallFor }), extra: { actionId: id, blurb: MINE_ROW_BLURB[id] } });
  return [make("detect_mine", intentCost(buildDetectMineIntent(c.id)).points), make("disarm_mine", intentCost(buildDisarmMineIntent(c.id, "")).points)];
}

/**
 * The grapple follow-up (3.3): the root's top rows while the unit is Grappled, the same choice the
 * sheet's follow-up button offers. The grapple's attacker (`Grappled.source` is itself) may Release
 * (free, Core p.72); the defender may Break free (contested HULL, a Quick action). A same-Size pair
 * also gets the start-of-turn Contest (Core p.69), shown only while the engine would take it
 * (`checkGrappleContestLegality`), since outside that window it is no choice at all. Break's
 * legality is `checkGrappleBreakLegality` over the pre-activation fold.
 */
function grappleRows(args: { model: UnitActionData; combatant: Combatant; state: EncounterState; wallFor: WallFor }): CommandMenuRow[] {
  const { model, combatant: c, state, wallFor } = args;
  if (!model.grappleMarker) return [];
  const partnerId = grapplePartner(state, c.id);
  const partner = model.grapplePartnerName;
  const make = (id: GrappleRowId, diamonds: number, verdict: Verdict) =>
    row({ id, kind: "action", label: GRAPPLE_ROW_LABEL[id], diamonds, verdict, extra: { actionId: id, blurb: grappleRowBlurb(id, partner), detail: grappleRowDetail(partner) } });
  const rows: CommandMenuRow[] = [];
  if (model.isGrappleAttacker) rows.push(make("grapple_release", 0, offCatalogVerdict({ model, id: "grapple_release", diamonds: 0, wallFor })));
  else {
    const v = offCatalogVerdict({ model, id: "grapple_break", diamonds: 1, wallFor });
    rows.push(make("grapple_break", 1, v.reason ? v : checkGrappleBreakLegality(state, c.id).ok ? {} : { reason: "Not this unit's turn" }));
  }
  const other = partnerId ? state.combatants[partnerId] : undefined;
  if (other && other.base.size === c.base.size && model.economy.economyLive && checkGrappleContestLegality(state, c.id).ok) {
    rows.push(make("grapple_contest", 0, offCatalogVerdict({ model, id: "grapple_contest", diamonds: 0, wallFor })));
  }
  return rows;
}

/**
 * The Full Tech card while its lock is open (3.3): the options (the four, any "Invade ›" rows, the
 * carried Quick Tech actions) as direct picks that continue the lock (`continuation`), free, with
 * the lock's progress. It reads the server-synced lock, so a menu opened mid-Full-Tech (reopened,
 * reloaded, a second client) lands here with the right count.
 * An NPC's fired option is greyed (an Invade greys its "Invade ›" rows too): its two Quick Techs
 * must differ (Core p.280).
 */
function liveFullTechScreen(args: { model: UnitActionData; combatant: Combatant; lock: AttackLock; title: string }): CommandMenuScreen {
  const npc = args.combatant.kind === "npc";
  const verdict: Verdict = {};
  const rows = [
    ...legalQuickTechOptions({ npcActor: npc }).map((o) => row({ id: o.id, kind: "option", label: o.name, diamonds: 0, verdict, extra: { actionId: o.id, continuation: true } })),
    ...invadeOptionRows({ model: args.model, verdict, diamonds: 0, continuation: true }),
    ...carriedQuickTechRows({ model: args.model, verdict, diamonds: 0, continuation: true }),
  ].map((r) => (npc && r.actionId !== undefined && args.lock.weapons.includes(techMenuPick(r.actionId).option) ? { ...r, available: false, reason: FULL_TECH_REPEAT } : r));
  return { id: "full_tech", title: args.title, parent: "hack", rows, fullTech: { fired: [...args.lock.weapons], count: 2 } };
}

/**
 * A pilot's Fight weapon screen (`pilot-command-menu` Q4): one row per weapon (each its own mount,
 * {@link mountKeyOf}), priced by the engine's own `intentCost` for a Fight with that weapon (a
 * Sidearm Fight is Quick, any other Full), so each row checks the diamond budget itself. `parent`
 * is Fight's own verdict without the budget. A weaponless pilot gets the unarmed strike. Every row
 * marks `fight` in the ledger ({@link CommandMenuRow.usedKey}).
 */
function fightRows(args: { model: UnitActionData; combatant: Combatant; state: EncounterState; parent: Verdict }): CommandMenuRow[] {
  const { model, combatant: c, state, parent } = args;
  const priceOf = (weapon: string | undefined) => intentCost(buildAttackIntent(c.id, { kind: "unit", id: c.id }, weapon !== undefined ? { weapon } : {}), model.overlay, state).points;
  if ((c.base.weapons ?? []).length === 0) {
    const diamonds = priceOf(undefined);
    return [{ ...unarmedStrikeRow({ parent: priced(model, parent, diamonds), diamonds }), usedKey: "fight" }];
  }
  return mountRows({ combatant: c, state, parent, diamonds: 0, compendium: model.compendium, barrage: false }).map((r) => {
    const diamonds = priceOf(r.weapons?.[0]?.key);
    const verdict = priced(model, verdictOf(r), diamonds);
    const { reason: _reason, cantAfford: _cantAfford, blockedBy: _blockedBy, ...rest } = r;
    return { ...rest, diamonds, available: verdict.reason === undefined, ...(verdict.reason !== undefined ? { reason: verdict.reason } : {}), ...(verdict.cantAfford ? { cantAfford: true as const } : {}), ...(verdict.blockedBy ? { blockedBy: verdict.blockedBy } : {}), usedKey: "fight" };
  });
}

/** Reload's reason when no Loading weapon is unloaded (`pilot-command-menu` Q5). */
export const NOTHING_TO_RELOAD = "Nothing to reload";

/** The pilot's Loading weapons the engine would reload right now (`checkReloadLegality` per weapon). */
function reloadable(state: EncounterState, c: Combatant): WeaponProfile[] {
  return (c.base.weapons ?? []).filter((w) => w.loading && !isItemDestroyed(c.current.resources, weaponInstanceKey(w)) && checkReloadLegality(state, c.id, weaponInstanceKey(w)).ok);
}

/**
 * The reasons a pilot's Mount, Jockey, Ride and Dismount (ally) rows are greyed when the verb has
 * no legal target (Core pp.70, 75). Each is read off the engine's own legality check.
 */
export const PILOT_VERB_REASON = {
  mount: "No Unpiloted mech of yours adjacent",
  jockey: "No enemy mech adjacent",
  jockeying: "Already jockeying a mech",
  ride: "No allied mech adjacent",
  riding: "Already riding a mech",
  ride_dismount: "Only while riding an ally",
} as const;

/**
 * Why a pilot's Mount, Jockey, Ride or Dismount (ally) has nothing to act on, or `undefined` when
 * the engine's own check passes for some unit on the board. Dismount (ally)'s landing cell is
 * picked on the board, so its row asks only whether the pilot is riding.
 */
function pilotVerbWall(state: EncounterState, c: Combatant, id: string): string | undefined {
  const others = () => Object.values(state.combatants).filter((o) => o.id !== c.id);
  switch (id) {
    case "mount":
      return others().some((o) => checkMountLegality(state, c.id, o.id).ok) ? undefined : PILOT_VERB_REASON.mount;
    case "jockey":
      if (jockeyPartner(state, c.id) !== undefined) return PILOT_VERB_REASON.jockeying;
      return others().some((o) => checkJockeyLegality(state, c.id, o.id).ok) ? undefined : PILOT_VERB_REASON.jockey;
    case "ride":
      if (ridePartner(state, c.id) !== undefined || jockeyPartner(state, c.id) !== undefined) return PILOT_VERB_REASON.riding;
      return others().some((o) => checkRideLegality(state, c.id, o.id).ok) ? undefined : PILOT_VERB_REASON.ride;
    case "ride_dismount":
      return ridePartner(state, c.id) !== undefined ? undefined : PILOT_VERB_REASON.ride_dismount;
    default:
      return undefined;
  }
}

/** The Jockey follow-up rows' ids (`pilot-command-menu` Q6): a Jockeying rider's three options. */
export type JockeyRowId = "jockey_distract" | "jockey_shred" | "jockey_damage";

/** The Jockey option each follow-up row fires (`request_jockey_menu`'s `choice`). */
export const JOCKEY_ROW_CHOICE: Readonly<Record<JockeyRowId, JockeyMenuChoice>> = {
  jockey_distract: "distract",
  jockey_shred: "shred",
  jockey_damage: "damage",
};

/** The Jockey follow-up rows' copy (`rulesCopyRegistry`'s `command-menu-rows`; Core p.75). */
export const JOCKEY_ROW_LABEL: Readonly<Record<JockeyRowId | "shake_off", string>> = {
  jockey_distract: "Distract",
  jockey_shred: "Shred",
  jockey_damage: "Damage",
  shake_off: "Shake Off",
};

export const JOCKEY_ROW_BLURB: Readonly<Record<JockeyRowId | "shake_off", string>> = {
  jockey_distract: "The mech is Impaired and Slowed until the end of its next turn.",
  jockey_shred: "Deal 2 heat to the mech.",
  jockey_damage: "Deal 4 kinetic damage to the mech.",
  shake_off: "A contested HULL check against the rider's GRIT, ties to the mech: on a success, the rider is thrown off.",
};

/** The Jockey follow-up rows' detail lines, naming the partner. */
export function jockeyingDetail(carrier: string): string {
  return `Jockeying ${carrier}`;
}
export function jockeyedDetail(rider: string): string {
  return `Jockeyed by ${rider}`;
}

/**
 * The Jockey rows at the root's top (`pilot-command-menu` Q6), in the grapple follow-up's shape: a
 * Jockeying rider's Distract, Shred and Damage, and a Jockeyed unit's Shake Off. Each is priced by
 * `intentCost` for its intent and greyed by the engine's own pair check over the pre-activation
 * fold (`checkJockeyMenuLegality`, `checkShakeOffLegality`) before the budget.
 */
function jockeyRows(args: { model: UnitActionData; combatant: Combatant; state: EncounterState; wallFor: WallFor }): CommandMenuRow[] {
  const { model, combatant: c, state, wallFor } = args;
  const nameOf = (id: string) => state.combatants[id]?.name ?? id;
  const make = (id: JockeyRowId | "shake_off", { diamonds, legal, detail }: { diamonds: number; legal: Result<void>; detail: string }) => {
    const wall = wallFor(id);
    const verdict: Verdict = wall ?? (!legal.ok ? { reason: legal.error } : priced(model, {}, diamonds));
    return row({ id, kind: "action", label: JOCKEY_ROW_LABEL[id], diamonds, verdict, extra: { actionId: id, blurb: JOCKEY_ROW_BLURB[id], detail } });
  };
  const rows: CommandMenuRow[] = [];
  const carrier = jockeyPartner(state, c.id);
  if (carrier !== undefined) {
    const legal = checkJockeyMenuLegality(state, c.id);
    for (const id of Object.keys(JOCKEY_ROW_CHOICE) as JockeyRowId[]) {
      rows.push(make(id, { diamonds: intentCost(buildJockeyMenuIntent(c.id, JOCKEY_ROW_CHOICE[id])).points, legal, detail: jockeyingDetail(nameOf(carrier)) }));
    }
  }
  const rider = jockeyedRiderOf(state, c.id);
  if (rider !== undefined) rows.push(make("shake_off", { diamonds: intentCost(buildShakeOffIntent(c.id)).points, legal: checkShakeOffLegality(state, c.id), detail: jockeyedDetail(nameOf(rider)) }));
  return rows;
}

/** The prepared-hold rows' ids and copy (`pilot-command-menu` Q7). */
export type HoldRowId = "release" | "drop_prepare";
export const HOLD_ROW_LABEL: Readonly<Record<HoldRowId, string>> = { release: "Release", drop_prepare: "Drop" };
export const HOLD_ROW_BLURB: Readonly<Record<HoldRowId, string>> = {
  release: "Take the held action now, as a reaction: pick its target on the board.",
  drop_prepare: "Drop the held action: your reactions come back, and the action spent to prepare it is not refunded.",
};
/** Release's reason for a hold the browser cannot release (a CLI-armed granted action or a Scan / Lock On / Bolster). */
export const RELEASE_FROM_CLI = "Release this hold from the CLI (lvtt encounter release)";

/**
 * Release and Drop, the root's top rows while a pilot holds a prepared action (Q7): its sheet
 * shows the hold read-only (`unit-sheet-redesign` Q2). Release is live for an attack-shaped hold
 * (`isAttackShapedHeld`, the check the room's `resolveRelease` makes); Drop always, since
 * `drop_prepare` takes any hold. Neither spends a diamond.
 */
function holdRows(args: { combatant: Combatant; held: HeldAction }): CommandMenuRow[] {
  const { combatant: c, held } = args;
  const detail = `${heldSummaryLine(held, c.base.weapons ?? [])} · “${held.triggerText}”`;
  const make = (id: HoldRowId, verdict: Verdict) => row({ id, kind: "action", label: HOLD_ROW_LABEL[id], diamonds: 0, verdict, extra: { actionId: id, blurb: HOLD_ROW_BLURB[id], detail } });
  // Release is a reaction, not a free action: it carries no FREE chip.
  return [{ ...make("release", isAttackShapedHeld(held) ? {} : { reason: RELEASE_FROM_CLI }), free: false }, make("drop_prepare", {})];
}

/** The line a menu whose turn isn't open says when no explore state names one (a fallback: the host always passes it). */
const TURN_NOT_OPEN = "Can't act right now";

/**
 * The one wall that holds for the whole unit (R1), in the room's gate order: a held prepared action
 * (the lockout, gate-ladder arm 1), then Stunned, Shut Down, Down and Out or a Black Thumb Rodeo (the
 * status wall), then
 * its turn not being open (the explore state's line). `undefined` when nothing does.
 */
function unitWall(args: { model: UnitActionData; combatant: Combatant; menuState?: CommandMenuState | undefined }): { line: string; kind: "held" | "stunned" | "turn" } | undefined {
  const { model, combatant: c, menuState } = args;
  // The held action's name, without its weapon (`heldSummaryLine` over no weapons).
  if (model.held) return { line: heldLine(heldSummaryLine(model.held, [])), kind: "held" };
  if (isStunned(c) || hasStatus(c.statuses, DOWN_AND_OUT_STATUS)) {
    const line = hasStatus(c.statuses, DOWN_AND_OUT_STATUS) ? UNIT_LINE.downAndOut : hasStatus(c.statuses, SHUT_DOWN_STATUS) ? UNIT_LINE.shutDown : UNIT_LINE.stunned;
    return { line, kind: "stunned" };
  }
  // The rodeo's idle wall is the same status wall: the rows it leaves are the rodeo's own.
  if (hasStatus(c.statuses, RODEO_STATUS)) return { line: UNIT_LINE.rodeo, kind: "stunned" };
  if (!model.economy.economyLive) return { line: menuState?.kind === "explore" ? menuState.reason : TURN_NOT_OPEN, kind: "turn" };
  return undefined;
}

/**
 * Build the menu tree for `combatant` off its action model. `state` is the raw encounter; the
 * economy reads use `model.economy.state` (the pre-activation fold). The menu only opens on a unit
 * the viewer controls (`commandMenuStateFor`), so nothing here asks. `menuState` is that state: an
 * explore menu's reason is the unit line while its turn isn't open, and a DONE unit's menu is
 * compact (F1).
 */
export function commandMenuTree(args: {
  model: UnitActionData;
  combatant: Combatant;
  state: EncounterState;
  menuState?: CommandMenuState;
  /** This unit's open attack lock (`activeAttack` while it acts), which the fire card reads. */
  lock?: AttackLock | null;
  /** The menu's local mount commitment before the first shot. */
  commit?: FireCommit | null;
  /** The chooser armed on this unit (`commandMenuChooserFor`), whose card the tree then holds. */
  chooser?: CommandMenuChooserId | null;
  /**
   * The GM's override mode (`decisions/0209`): the menu is never compact, and every row that
   * sends a forceable intent is available, so the GM can force any of them. The unforceable rows
   * (Boost, Prepare, the pilot-bridge and identity verbs) keep their verdicts, the unit wall
   * included.
   */
  override?: boolean;
}): CommandMenuTree {
  const { model, combatant: c, state, menuState } = args;
  const econState = model.economy.state;
  const wall = unitWall({ model, combatant: c, menuState });
  const wallVerdict: Verdict | undefined = wall && { reason: wall.line, blockedBy: "unit" };
  const live = model.economy.economyLive;
  // A Stunned unit on its own turn keeps the rows the status wall excepts; every other wall blocks every row.
  const wallFor: WallFor = (id) => (wall?.kind === "stunned" && live && actionStatusBlockReason(state, c.id, id) === null ? undefined : wallVerdict);
  const econCombatant = econState.combatants[c.id] ?? c;
  const screens = {} as CommandMenuTree["screens"];
  const groupRows: Record<CommandMenuGroupId, CommandMenuRow[]> = { attack: [], hack: [], maneuver: [], equipment: [], system: [] };
  const npc = c.kind === "npc";
  // An NPC's feature text carries tier tokens (`{2/3/4}`, `{speed}`), resolved at the unit's tier
  // against its class's stats, as the compendium sheet resolves them.
  const npcStats = npc && c.sourceRef ? (model.compendium.getById("npc_class", c.sourceRef) as { stats?: Record<string, unknown> } | undefined)?.stats : undefined;
  const blurbOf = (text: string): string => (npc ? interpolateTierTokens(text, asNpcTier(c.tier), npcStats) : text);

  // Core rows, in CORE_GROUP order.
  const coreById = new Map(model.views.map((v) => [v.id, v]));
  for (const [id, group] of CORE_GROUP) {
    const v = coreById.get(id);
    if (!v) continue;
    // A live Backswing Cut reservation re-lights Skirmish as a free follow-up (no action spent).
    const backswingFree = id === "skirmish" && model.backswingWeaponId !== undefined;
    let diamonds = backswingFree ? 0 : diamondsFor(v.cost);
    // A pilot's Fight and Reload price per weapon, so their own verdict leaves the budget to the
    // rows; Mount, Jockey, Ride and Dismount (ally) check for a legal target before the budget.
    const rowOwn = rowVerdict({ model, combatant: econCombatant, view: v });
    const verbWall = rowOwn.reason ? undefined : pilotVerbWall(econState, econCombatant, id);
    const specific: Verdict = verbWall !== undefined ? { reason: verbWall } : rowOwn;
    const unpriced = gate(wallFor(id), specific);
    const own: Verdict = id === "fight" || id === "reload" ? unpriced : gate(wallFor(id), priced(model, specific, diamonds));
    // Boost drills to its form card only while it carries opt-in riders to pick.
    const boostForm = id === "boost" && (v.optInRiders?.length ?? 0) > 0;
    const childId: CommandMenuScreenId | undefined = boostForm ? "boost" : CORE_CHILD[id];
    let children: CommandMenuRow[] = [];
    if (id === "fight") {
      children = fightRows({ model, combatant: econCombatant, state: econState, parent: own });
      diamonds = Math.min(...children.map((r) => r.diamonds));
    } else if (childId === "skirmish") {
      children = mountRows({ combatant: econCombatant, state: econState, parent: own, diamonds: backswingFree ? 0 : 1, compendium: model.compendium, barrage: false, reserved: model.backswingWeaponId });
      if ((econCombatant.base.weapons ?? []).length === 0) children = [unarmedStrikeRow({ parent: own, diamonds: 1 })];
    }
    if (childId === "barrage") children = mountRows({ combatant: econCombatant, state: econState, parent: own, diamonds: 0, compendium: model.compendium, barrage: true });
    if (childId === "quick_tech") children = techOptionRows({ model, combatant: econCombatant, parent: own, diamonds: 1 });
    if (childId === "full_tech") children = techOptionRows({ model, combatant: econCombatant, parent: own, diamonds: 0, fullTech: true });
    // Reload (Q5): one click on the one unloaded Loading weapon, a card listing them when two or
    // more are, greyed when none is.
    let reloadRow: Partial<CommandMenuRow> | undefined;
    let reloadVerdict: Verdict | undefined;
    if (id === "reload") {
      const weapons = reloadable(econState, econCombatant).map((w) => mountWeapon({ weapon: w, combatant: econCombatant, state: econState, compendium: model.compendium, barrage: false }));
      const perWeapon = priced(model, own, diamonds);
      if (weapons.length >= 2) {
        children = weapons.map((w) => row({ id: w.key, kind: "action", label: w.name, diamonds, verdict: perWeapon, extra: { actionId: "reload", weapons: [w], usedKey: "reload", ...(w.range ? { secondary: w.range } : {}) } }));
        screens.reload = { id: "reload", title: v.name, parent: group, rows: children };
        reloadRow = { child: "reload" };
        reloadVerdict = withChildren(own, children);
      } else {
        reloadRow = weapons[0] ? { weapons: [weapons[0]], detail: weapons[0].name } : {};
        reloadVerdict = ownReason(own) ? own : weapons.length === 0 && live ? { reason: NOTHING_TO_RELOAD } : perWeapon;
      }
    }
    if (boostForm) screens.boost = { id: "boost", title: v.name, parent: group, rows: [], form: true };
    else if (childId) {
      screens[childId] = {
        id: childId,
        title: v.name,
        parent: group,
        rows: children,
        ...(childId === "barrage" ? { pick: { count: 2, repeat: false, confirmLabel: npc ? "Confirm weapons" : "Confirm mounts", ...(npc ? { noun: "weapon" as const } : {}), diamonds: 2 } } : {}),
        ...(childId === "full_tech" ? { pick: { count: 2, repeat: !npc, confirmLabel: "Run both", diamonds: 2 } } : {}),
      };
    }
    groupRows[group].push(
      row({
        id,
        kind: "action",
        label: v.name,
        diamonds,
        protocol: isProtocolCost(v.cost),
        verdict: reloadVerdict ?? (childId && !boostForm ? withChildren(own, children) : own),
        // Off-turn the ledger is last turn's: nothing reads struck (R3).
        usedThisTurn: live && v.usedUp && !backswingFree,
        extra: {
          actionId: id === "stabilize" && npc ? NPC_STABILIZE_ROW : id,
          blurb: blurbOf(v.blurb),
          ...(childId ? { child: childId } : {}),
          ...(backswingFree ? { free: true } : {}),
          ...(id === "overcharge" ? { detail: overchargeRowDetail(overchargeStep(econCombatant)) } : {}),
          ...(id === "stabilize" && npc ? { detail: NPC_STABILIZE_DETAIL } : {}),
          ...reloadRow,
        },
      }),
    );
  }

  // The mine rows, right after Search.
  const searchAt = groupRows.maneuver.findIndex((r) => r.id === "search");
  groupRows.maneuver.splice(searchAt + 1, 0, ...mineRows({ model, combatant: econCombatant, wallFor }));

  // Granted rows: equipment grants to Use Equipment, the rest to Mech System with the core power
  // first and ahead of the core system rows (R6).
  const powerName = corePowerName(model, c);
  const grantedSystem: CommandMenuRow[] = [];
  for (const v of model.grantedViews) {
    const diamonds = diamondsFor(v.cost);
    const feature = featureState({ combatant: econCombatant, state: econState, key: v.id });
    const r = row({
      id: v.id,
      kind: "action",
      label: v.name,
      diamonds,
      protocol: isProtocolCost(v.cost),
      verdict: gate(wallFor(v.id), priced(model, rowVerdict({ model, combatant: econCombatant, view: v, feature }), diamonds)),
      usedThisTurn: live && v.usedUp,
      extra: {
        actionId: v.id,
        blurb: blurbOf(v.blurb),
        ...(feature.recharge ? { recharge: feature.recharge } : {}),
        ...(feature.uses && feature.uses.left > 0 ? { detail: usesLeftLine(feature.uses) } : {}),
      },
    });
    if (grantedGroup(model, c, v) === "equipment") groupRows.equipment.push(r);
    else grantedSystem.push(r);
  }
  grantedSystem.sort((a, b) => Number(b.label === powerName) - Number(a.label === powerName));
  groupRows.system = [...grantedSystem, ...groupRows.system];
  // Section each group screen (`decisions/0186` item 2): Start of turn, then Free, then Quick and
  // Full, the order above kept inside a section (the sort is stable), so the core power leads.
  for (const g of GROUPS) {
    for (const r of groupRows[g.id]) r.section = sectionOf(r);
    groupRows[g.id].sort((a, b) => SECTION_RANK[sectionOf(a)] - SECTION_RANK[sectionOf(b)]);
  }
  // An NPC's Features follow its class's feature order, base before optional, which says nothing to
  // the table; inside a section they read Quick before Full instead (Charge, then Impale).
  if (npc) groupRows.equipment.sort((a, b) => SECTION_RANK[sectionOf(a)] - SECTION_RANK[sectionOf(b)] || a.diamonds - b.diamonds);

  // protocolBelow on every drilling row, then the group screens and the root.
  const hasAvailableProtocol = (rows: readonly CommandMenuRow[]): boolean =>
    rows.some((r) => (r.protocol && r.available) || r.protocolBelow);
  for (const rows of Object.values(groupRows)) {
    for (const r of rows) if (r.child) r.protocolBelow = hasAvailableProtocol(screens[r.child]?.rows ?? []);
  }
  const groupRootRows: CommandMenuRow[] = groupsOf(c).map((g) => {
    const rows = groupRows[g.id];
    const title = groupTitle(c, g);
    screens[g.id] = { id: g.id, title, parent: "root", rows };
    return row({ id: g.id, kind: "group", label: title, diamonds: 0, verdict: withChildren({}, rows), protocolBelow: hasAvailableProtocol(rows), extra: { child: g.id } });
  });
  // The live follow-ups head the root: a held grapple's (3.3), a Jockey pair's (`pilot-command-menu`
  // Q6), and a pilot's prepared hold (Q7; the other kinds' sheets keep their hold controls).
  const held = c.kind === "pilot" ? model.held : null;
  const followUps = [
    ...(held ? holdRows({ combatant: econCombatant, held }) : []),
    ...grappleRows({ model, combatant: econCombatant, state: econState, wallFor }),
    ...jockeyRows({ model, combatant: econCombatant, state: econState, wallFor }),
  ];
  // Compact (F1, F3): a DONE unit's menu has no rows; a Stunned, Shut Down or holding unit's holds,
  // flat, only the rows the wall leaves it (Boot Up, Dismount, Eject; Release and Drop), less any
  // its own state rules out (Boot Up on a unit that isn't Shut Down).
  const compact: CommandMenuTree["compact"] =
    args.override === true ? undefined : wall?.kind === "held" || wall?.kind === "stunned" ? "wall" : menuState?.kind === "explore" && menuState.headline === "DONE" ? "done" : undefined;
  const isHoldRow = (r: CommandMenuRow) => r.id === "release" || r.id === "drop_prepare";
  const stillTakeable = (r: CommandMenuRow) => isHoldRow(r) || (wallFor(r.id) === undefined && (r.available || r.cantAfford === true || r.usedThisTurn || !ownReason(verdictOf(r))));
  const rootRows =
    compact === "done"
      ? []
      : compact === "wall"
        ? [...followUps, ...groupsOf(c).flatMap((g) => groupRows[g.id].filter((r) => !r.child))].filter(stillTakeable).map(({ section: _section, ...r }) => r)
        : [...followUps, ...groupRootRows];
  screens.root = { id: "root", title: "", rows: rootRows };
  // A unit with no Full Tech descriptor has no `full_tech` screen to make live.
  if (args.lock?.action === "full_tech" && screens.full_tech) screens.full_tech = liveFullTechScreen({ model, combatant: c, lock: args.lock, title: screens.full_tech.title });
  // A pilot's Fight is its Skirmish here: the fire card it opens is the same card.
  const skirmishView = coreById.get("skirmish") ?? coreById.get("fight");
  const fire = fireScreen({
    combatant: econCombatant,
    state: econState,
    lock: args.lock ?? null,
    commit: args.commit ?? null,
    skirmishUsable: skirmishView !== undefined && !skirmishView.usedUp,
    compendium: model.compendium,
    reserved: model.backswingWeaponId,
    overlay: model.overlay,
  });
  if (fire) screens.fire = fire;
  if (args.override === true) liftForceable(screens);
  // The armed chooser's card, under the group that holds its row.
  const chooserGroup = args.chooser === "disarm_mine" ? "maneuver" : args.chooser ? CORE_GROUP.find(([id]) => id === args.chooser)?.[1] : undefined;
  const chooserTitle = args.chooser === "disarm_mine" ? MINE_ROW_LABEL.disarm_mine : args.chooser ? coreById.get(args.chooser)?.name : undefined;
  // An NPC's Stabilize fires on the click (its choice is fixed), so it has no chooser card.
  if (args.chooser && chooserGroup && chooserTitle !== undefined && !(npc && args.chooser === "stabilize")) {
    screens[args.chooser] = { id: args.chooser, title: chooserTitle, parent: chooserGroup, rows: [], chooser: true };
  }

  const protocolLegendCount = Object.values(screens)
    .flatMap((s) => s.rows)
    .filter((r) => r.protocol && r.available).length;

  // The unit's reaction while it is not acting (F2): Overwatch, Brace and its granted reactions, and whether its one
  // reaction a round is left. A held action or the status wall takes reactions too (Core p.78).
  const reaction = model.isActiveActor ? undefined : { open: model.reactionsLeft > 0 && wall?.kind !== "held" && wall?.kind !== "stunned", names: [...model.reactionActions, ...model.grantedReactions].map((a) => a.name) };

  return {
    screens,
    diamondsLeft: live ? model.economy.ap : 0,
    ...(model.economy.budget ? { moveLeft: model.economy.budget.move } : {}),
    ...(groupRows.maneuver.find((r) => r.id === "boost")?.available ? { boostMove: econCombatant.base.speed } : {}),
    protocolLegendCount: compact ? 0 : protocolLegendCount,
    // The live state, not the fold: a ready unit is not acting, so its window reads open.
    protocolWindowClosed: !isTurnVirgin(state, c.id),
    endTurn: live,
    actionsLeft: live && actionsLeftToEnd({ ap: model.economy.ap, combatant: c }),
    counters: live,
    ...(args.override === true ? { unitLine: OVERRIDE_UNIT_LINE } : wall ? { unitLine: wall.line } : {}),
    ...(compact ? { compact } : {}),
    ...(reaction ? { reaction } : {}),
  };
}

/**
 * The ledger key an action marks used when it resolves (`projects/token-command-menu.md`, the v19
 * landing ruling): a Skirmish mount marks `skirmish`; a Quick Tech option marks its own option; the
 * Barrage and Full Tech confirms mark `barrage` and `full_tech`; every other row marks its own id.
 */
export function usedKeyOf(row: CommandMenuRow, screen: CommandMenuScreenId): string {
  if (row.usedKey !== undefined) return row.usedKey;
  if (row.kind === "mount") return screen === "barrage" ? "barrage" : "skirmish";
  if (row.id === UNARMED_STRIKE_ROW) return "skirmish";
  return row.id;
}

/**
 * The row that opens `screen` and the screen it sits on (a pilot's Fight row opens the `skirmish`
 * screen from Attack), or `undefined` when no row drills into it.
 */
export function openerOf(tree: CommandMenuTree, screen: CommandMenuScreenId): { screen: CommandMenuScreenId; row: CommandMenuRow } | undefined {
  for (const s of Object.values(tree.screens)) {
    const row = s.rows.find((r) => r.child === screen);
    if (row) return { screen: s.id, row };
  }
  return undefined;
}

/**
 * Where the menu lands once an action resolves (v19): the screen whose rows include the row that
 * `usedKey` marks, so the player sees it struck through (Skirmish, Barrage, Ram → Attack; a Quick
 * Tech option → Quick Tech; Full Tech → Hack; Boost → Maneuver). Root when no screen holds it.
 */
export function homeOf(tree: CommandMenuTree, usedKey: string): CommandMenuScreenId {
  // A compact menu is its root card alone.
  if (tree.compact) return "root";
  for (const screen of Object.values(tree.screens)) {
    // Pick screens (Barrage mounts, Full Tech options) hold toggles, never a used row; the fire
    // card holds weapons; a chooser's card holds none.
    if (screen.id === "root" || screen.id === "fire" || screen.pick || screen.chooser || screen.form || screen.fullTech) continue;
    if (screen.rows.some((r) => r.id === usedKey && r.kind !== "mount")) return screen.id;
  }
  return "root";
}
